import { useCallback, useEffect, useState } from 'react';
import {
    Box,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Skeleton,
    Tooltip,
    Typography,
} from '@mui/material';
import WbSunny from '@mui/icons-material/WbSunny';
import NightsStay from '@mui/icons-material/NightsStay';
import Cloud from '@mui/icons-material/Cloud';
import FilterDrama from '@mui/icons-material/FilterDrama';
import Grain from '@mui/icons-material/Grain';
import Umbrella from '@mui/icons-material/Umbrella';
import AcUnit from '@mui/icons-material/AcUnit';
import Thunderstorm from '@mui/icons-material/Thunderstorm';
import BlurOn from '@mui/icons-material/BlurOn';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloseIcon from '@mui/icons-material/Close';
import { apiFetch } from '../lib/api';
import { TIMEZONE } from '../lib/time';

/**
 * Current conditions and the week ahead, for a work location.
 *
 * Genuinely operational for this business rather than decoration: traffic
 * management and civil crews stand down in inclement weather, and `staff`
 * carries an `inclement_weather` pay condition that depends on it.
 */

export interface WeatherDay {
    readonly date: string;
    readonly high: number;
    readonly low: number;
    readonly precipitationChance: number;
    readonly code: number;
    readonly description: string;
}

export interface Weather {
    readonly location: string;
    readonly temperature: number;
    readonly apparentTemperature: number;
    readonly humidity: number;
    readonly windSpeed: number;
    readonly windDirection: string;
    readonly high: number;
    readonly low: number;
    readonly precipitationChance: number;
    readonly code: number;
    readonly description: string;
    readonly isDay: boolean;
    readonly observedAt: string;
    readonly forecast: readonly WeatherDay[];
}

/** WMO code to icon. Colours are fixed, not themed — a sun is yellow in any theme. */
function WeatherIcon({ code, isDay, size = 40 }: { code: number; isDay?: boolean; size?: number }): React.JSX.Element {
    const sx = { fontSize: size } as const;
    if (code === 0) {
        return isDay !== false ? (
            <WbSunny sx={{ ...sx, color: '#ffeb3b' }} />
        ) : (
            <NightsStay sx={{ ...sx, color: '#7986cb' }} />
        );
    }
    if (code <= 2) {
        return <FilterDrama sx={{ ...sx, color: '#cfd8dc' }} />;
    }
    if (code === 3) {
        return <Cloud sx={{ ...sx, color: '#90a4ae' }} />;
    }
    if (code <= 48) {
        return <BlurOn sx={{ ...sx, color: '#9e9e9e' }} />;
    }
    if (code <= 57) {
        return <Grain sx={{ ...sx, color: '#4fc3f7' }} />;
    }
    if (code <= 82) {
        return <Umbrella sx={{ ...sx, color: '#4fc3f7' }} />;
    }
    if (code <= 86) {
        return <AcUnit sx={{ ...sx, color: '#b3e5fc' }} />;
    }
    return <Thunderstorm sx={{ ...sx, color: '#ffa726' }} />;
}

/** Label above value — the compact stat used across the header strip. */
function Stat({ label, value }: { label: string; value: string }): React.JSX.Element {
    return (
        <Box sx={{ minWidth: 40 }}>
            <Typography
                sx={{
                    color: 'text.secondary',
                    fontSize: '0.6rem',
                    fontWeight: 500,
                    lineHeight: 1,
                    mb: 0.4,
                    opacity: 0.7,
                    whiteSpace: 'nowrap',
                }}
            >
                {label}:
            </Typography>
            <Typography
                sx={{
                    color: 'text.primary',
                    fontSize: '0.85rem',
                    fontWeight: 800,
                    lineHeight: 1,
                    whiteSpace: 'nowrap',
                }}
            >
                {value}
            </Typography>
        </Box>
    );
}

function VDivider(): React.JSX.Element {
    return (
        <Divider
            orientation="vertical"
            flexItem
            sx={{
                mx: 2,
                borderColor: 'rgba(255,255,255,0.15)',
                height: 40,
                alignSelf: 'center',
                display: { xs: 'none', lg: 'block' },
            }}
        />
    );
}

/**
 * Sample data for the dev-only design preview, which has no session and so
 * cannot call the authenticated weather endpoint. Never reached in a production
 * build — import.meta.env.DEV is statically false and this is tree-shaken out.
 */
const PREVIEW_SAMPLE: Weather = {
    location: 'Greensborough',
    temperature: 14.9,
    apparentTemperature: 11.4,
    humidity: 51,
    windSpeed: 16.7,
    windDirection: 'SE',
    high: 17,
    low: 7,
    precipitationChance: 20,
    code: 2,
    description: 'Partly cloudy',
    isDay: true,
    observedAt: new Date().toISOString(),
    forecast: [0, 1, 2, 3, 4, 5, 6].map((i) => {
        const d = new Date();
        d.setDate(d.getDate() + i);
        return {
            date: d.toISOString().slice(0, 10),
            high: 17 - i,
            low: 7 + (i % 3),
            precipitationChance: [20, 47, 10, 65, 30, 5, 15][i] ?? 0,
            code: [2, 3, 0, 61, 2, 0, 3][i] ?? 0,
            description: ['Partly cloudy', 'Overcast', 'Clear', 'Rain', 'Partly cloudy', 'Clear', 'Overcast'][i] ?? '',
        };
    }),
};

function formatObserved(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) {
        return '';
    }
    const date = new Intl.DateTimeFormat('en-AU', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        timeZone: TIMEZONE,
    }).format(d);
    const time = new Intl.DateTimeFormat('en-AU', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone: TIMEZONE,
    }).format(d);
    return `${date} at ${time}`;
}

function formatDay(isoDate: string, index: number): string {
    if (index === 0) {
        return 'Today';
    }
    const d = new Date(`${isoDate}T00:00:00`);
    return new Intl.DateTimeFormat('en-AU', { weekday: 'short', day: 'numeric', month: 'short' }).format(d);
}

export function WeatherBanner(): React.JSX.Element | null {
    const [weather, setWeather] = useState<Weather | null>(null);
    const [failed, setFailed] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [showMore, setShowMore] = useState(false);

    const load = useCallback((): void => {
        setRefreshing(true);
        apiFetch<Weather>('/weather')
            .then((data) => {
                setWeather(data);
                setFailed(false);
            })
            .catch(() => {
                if (import.meta.env.DEV) {
                    setWeather(PREVIEW_SAMPLE);
                    return;
                }
                setFailed(true);
            })
            .finally(() => {
                setRefreshing(false);
            });
    }, []);

    useEffect(() => {
        load();
        // The API caches upstream for 10 minutes, so polling faster only
        // re-reads the same cached response.
        const t = setInterval(load, 10 * 60_000);
        return () => {
            clearInterval(t);
        };
    }, [load]);

    // A weather outage should cost the dashboard nothing. Render nothing rather
    // than an error the user can neither act on nor dismiss.
    if (failed) {
        return null;
    }

    if (weather === null) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Skeleton variant="circular" width={40} height={40} sx={{ bgcolor: 'rgba(255,255,255,0.08)' }} />
                <Skeleton variant="text" width={180} sx={{ bgcolor: 'rgba(255,255,255,0.08)' }} />
            </Box>
        );
    }

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: { xs: 'flex-start', lg: 'flex-end' },
                    flexWrap: { xs: 'wrap', lg: 'nowrap' },
                    gap: { xs: 2, lg: 0 },
                }}
            >
                <VDivider />

                {/* Location + observation time */}
                <Box sx={{ minWidth: 'fit-content' }}>
                    <Typography
                        sx={{
                            fontWeight: 800,
                            fontSize: '0.85rem',
                            letterSpacing: '0.04em',
                            lineHeight: 1.2,
                            color: 'text.primary',
                        }}
                    >
                        {weather.location.toUpperCase()}
                    </Typography>
                    <Typography sx={{ color: 'text.secondary', fontSize: '0.75rem', opacity: 0.75 }}>
                        {formatObserved(weather.observedAt)}
                    </Typography>
                </Box>

                <VDivider />

                {/* Temperature */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WeatherIcon code={weather.code} isDay={weather.isDay} />
                    <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                        <Typography
                            sx={{
                                color: 'text.primary',
                                fontWeight: 300,
                                fontSize: '2.4rem',
                                lineHeight: 0.9,
                                letterSpacing: '-0.02em',
                            }}
                        >
                            {weather.temperature.toFixed(1)}
                        </Typography>
                        <Typography
                            sx={{ color: 'text.primary', fontSize: '0.65rem', fontWeight: 800, ml: 0.5, mt: 0.25 }}
                        >
                            °C
                        </Typography>
                    </Box>
                    <Typography sx={{ color: 'text.secondary', fontSize: '0.85rem', ml: 0.5, whiteSpace: 'nowrap' }}>
                        {weather.description}
                    </Typography>
                </Box>

                {/* Stats */}
                <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 2.5, ml: 3 }}>
                    <Stat label="Humidity" value={`${weather.humidity}%`} />
                    <Stat label="Direction" value={weather.windDirection} />
                    <Stat label="Wind" value={`${weather.windSpeed.toFixed(1)} km/h`} />
                    <Stat label="High" value={`${Math.round(weather.high)}°`} />
                    <Stat label="Low" value={`${Math.round(weather.low)}°`} />
                </Box>

                {/* Controls */}
                {/* Below lg these wrap to their own line, so no left offset. */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, ml: { xs: 0, lg: 2 } }}>
                    <Tooltip title="Refresh">
                        <IconButton
                            size="small"
                            onClick={load}
                            aria-label="Refresh weather"
                            sx={{
                                color: 'text.secondary',
                                '&:hover': { color: 'text.primary' },
                                animation: refreshing ? 'spin 0.8s linear infinite' : 'none',
                                '@keyframes spin': {
                                    from: { transform: 'rotate(0deg)' },
                                    to: { transform: 'rotate(360deg)' },
                                },
                            }}
                        >
                            <RefreshIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>

                    <Box
                        component="button"
                        type="button"
                        onClick={() => {
                            setShowMore(true);
                        }}
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.75,
                            px: 1.75,
                            py: 0.85,
                            borderRadius: 999,
                            border: '1px solid',
                            borderColor: 'rgba(255,255,255,0.35)',
                            bgcolor: 'transparent',
                            color: 'text.primary',
                            fontFamily: 'inherit',
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                            transition: 'all 0.15s ease',
                            '&:hover': { borderColor: 'common.white', bgcolor: 'rgba(255,255,255,0.06)' },
                        }}
                    >
                        Show More
                        <OpenInNewIcon sx={{ fontSize: '0.95rem' }} />
                    </Box>
                </Box>
            </Box>

            {/* ─── Week ahead ──────────────────────────────────────────────── */}
            <Dialog
                open={showMore}
                onClose={() => {
                    setShowMore(false);
                }}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
                    <Box>
                        <Typography sx={{ fontWeight: 800, fontSize: '1rem' }}>
                            {weather.location} — week ahead
                        </Typography>
                        <Typography sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                            Feels like {weather.apparentTemperature}° now · {weather.precipitationChance}% chance of
                            rain today
                        </Typography>
                    </Box>
                    <IconButton
                        onClick={() => {
                            setShowMore(false);
                        }}
                        size="small"
                        aria-label="Close"
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Box
                        sx={{
                            display: 'grid',
                            gap: 1.5,
                            gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)', md: 'repeat(7, 1fr)' },
                            pb: 1,
                        }}
                    >
                        {weather.forecast.map((day, i) => (
                            <Box
                                key={day.date}
                                sx={{
                                    p: 1.5,
                                    borderRadius: 2,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    textAlign: 'center',
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: '0.65rem',
                                        fontWeight: 700,
                                        textTransform: 'uppercase',
                                        letterSpacing: 0.5,
                                        color: i === 0 ? 'primary.light' : 'text.secondary',
                                        mb: 1,
                                    }}
                                >
                                    {formatDay(day.date, i)}
                                </Typography>
                                <WeatherIcon code={day.code} size={28} />
                                <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mt: 0.5, mb: 1 }}>
                                    {day.description}
                                </Typography>
                                <Typography sx={{ fontSize: '1rem', fontWeight: 800, lineHeight: 1 }}>
                                    {Math.round(day.high)}°
                                </Typography>
                                <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', lineHeight: 1.6 }}>
                                    {Math.round(day.low)}°
                                </Typography>
                                <Typography sx={{ fontSize: '0.7rem', color: '#4fc3f7', fontWeight: 600, mt: 0.5 }}>
                                    {day.precipitationChance}%
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </DialogContent>
            </Dialog>
        </>
    );
}
